import { useState, useEffect, useCallback, useMemo } from 'react';
import { breakpoints, BreakpointKey } from './useMediaQuery';

export interface ViewportState {
  width: number;
  height: number;
  orientation: 'portrait' | 'landscape';
  breakpoint: BreakpointKey;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWideScreen: boolean;
  isUltraWide: boolean;
  scrollY: number;
  scrollX: number;
}

interface UseViewportOptions {
  /** Debounce delay in ms for resize events (default: 100) */
  debounceDelay?: number;
  /** Whether to track scroll position (default: false) */
  trackScroll?: boolean;
  /** Throttle delay in ms for scroll events (default: 16 for 60fps) */
  scrollThrottleDelay?: number;
}

/**
 * Determine current breakpoint from width
 */
function getBreakpointFromWidth(width: number): BreakpointKey {
  if (width >= breakpoints['2k']) return '2k';
  if (width >= breakpoints['2xl']) return '2xl';
  if (width >= breakpoints.xlg) return 'xlg';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
}

/**
 * Custom hook to track viewport dimensions and breakpoints
 * @param options - Configuration options
 * @returns ViewportState object with current viewport info
 */
export function useViewport(options: UseViewportOptions = {}): ViewportState {
  const {
    debounceDelay = 100,
    trackScroll = false,
    scrollThrottleDelay = 16,
  } = options;

  const getInitialState = useCallback((): ViewportState => {
    if (typeof window === 'undefined') {
      return {
        width: 0,
        height: 0,
        orientation: 'portrait',
        breakpoint: 'xs',
        isMobile: true,
        isTablet: false,
        isDesktop: false,
        isWideScreen: false,
        isUltraWide: false,
        scrollY: 0,
        scrollX: 0,
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const breakpoint = getBreakpointFromWidth(width);

    return {
      width,
      height,
      orientation: width > height ? 'landscape' : 'portrait',
      breakpoint,
      isMobile: width < breakpoints.md,
      isTablet: width >= breakpoints.md && width < breakpoints.lg,
      isDesktop: width >= breakpoints.lg,
      isWideScreen: width >= breakpoints.xlg,
      isUltraWide: width >= breakpoints['2k'],
      scrollY: window.scrollY || window.pageYOffset || 0,
      scrollX: window.scrollX || window.pageXOffset || 0,
    };
  }, []);

  const [state, setState] = useState<ViewportState>(getInitialState);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let resizeTimeout: ReturnType<typeof setTimeout>;
    let scrollTimeout: ReturnType<typeof setTimeout>;
    let lastScrollTime = 0;

    const updateViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const breakpoint = getBreakpointFromWidth(width);

      setState(prev => ({
        ...prev,
        width,
        height,
        orientation: width > height ? 'landscape' : 'portrait',
        breakpoint,
        isMobile: width < breakpoints.md,
        isTablet: width >= breakpoints.md && width < breakpoints.lg,
        isDesktop: width >= breakpoints.lg,
        isWideScreen: width >= breakpoints.xlg,
        isUltraWide: width >= breakpoints['2k'],
      }));
    };

    const updateScroll = () => {
      setState(prev => ({
        ...prev,
        scrollY: window.scrollY || window.pageYOffset || 0,
        scrollX: window.scrollX || window.pageXOffset || 0,
      }));
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateViewport, debounceDelay);
    };

    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollTime >= scrollThrottleDelay) {
        lastScrollTime = now;
        updateScroll();
      }
    };

    // Initial update
    updateViewport();
    if (trackScroll) updateScroll();

    // Add event listeners
    window.addEventListener('resize', handleResize, { passive: true });
    if (trackScroll) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    // Also listen for orientation change on mobile
    window.addEventListener('orientationchange', handleResize, { passive: true });

    return () => {
      clearTimeout(resizeTimeout);
      clearTimeout(scrollTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (trackScroll) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [debounceDelay, trackScroll, scrollThrottleDelay]);

  return state;
}

/**
 * Hook to get safe area insets for notch/home indicator handling
 * @returns Object with safe area inset values
 */
export function useSafeAreaInsets() {
  const [insets, setInsets] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.CSS?.supports) return;

    // Check if safe-area-inset is supported
    if (!CSS.supports('padding-top', 'env(safe-area-inset-top)')) return;

    const updateInsets = () => {
      // Create a temporary element to measure safe area insets
      const temp = document.createElement('div');
      temp.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        padding-top: env(safe-area-inset-top);
        padding-bottom: env(safe-area-inset-bottom);
        padding-left: env(safe-area-inset-left);
        padding-right: env(safe-area-inset-right);
        pointer-events: none;
        visibility: hidden;
      `;
      document.body.appendChild(temp);

      const style = getComputedStyle(temp);
      setInsets({
        top: parseFloat(style.paddingTop) || 0,
        bottom: parseFloat(style.paddingBottom) || 0,
        left: parseFloat(style.paddingLeft) || 0,
        right: parseFloat(style.paddingRight) || 0,
      });

      document.body.removeChild(temp);
    };

    updateInsets();
    window.addEventListener('resize', updateInsets, { passive: true });
    window.addEventListener('orientationchange', updateInsets, { passive: true });

    return () => {
      window.removeEventListener('resize', updateInsets);
      window.removeEventListener('orientationchange', updateInsets);
    };
  }, []);

  return insets;
}

/**
 * Hook to detect if keyboard is visible (mobile)
 * @returns boolean indicating if virtual keyboard is likely visible
 */
export function useIsKeyboardVisible(): boolean {
  const [isVisible, setIsVisible] = useState(false);
  const viewport = useViewport();

  useEffect(() => {
    if (typeof window === 'undefined' || !viewport.isMobile) {
      setIsVisible(false);
      return;
    }

    // Use visualViewport API if available
    if (window.visualViewport) {
      const handleResize = () => {
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const windowHeight = window.innerHeight;
        // If visualViewport is significantly smaller than window, keyboard is likely open
        setIsVisible(windowHeight - viewportHeight > 150);
      };

      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }

    // Fallback: detect focus on input elements
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        setIsVisible(true);
      }
    };

    const handleBlur = () => {
      setIsVisible(false);
    };

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, [viewport.isMobile]);

  return isVisible;
}

/**
 * Hook to get responsive value based on current breakpoint
 * @param values - Object mapping breakpoints to values
 * @param defaultValue - Default value if no breakpoint matches
 * @returns The value for the current breakpoint
 */
export function useResponsiveValue<T>(
  values: Partial<Record<BreakpointKey, T>>,
  defaultValue: T
): T {
  const { breakpoint } = useViewport();

  return useMemo(() => {
    // Check breakpoints from largest to smallest
    const breakpointOrder: BreakpointKey[] = ['2k', '2xl', 'xlg', 'xl', 'lg', 'md', 'sm', 'xs'];
    const currentIndex = breakpointOrder.indexOf(breakpoint);

    // Find the first defined value at or below the current breakpoint
    for (let i = currentIndex; i < breakpointOrder.length; i++) {
      const bp = breakpointOrder[i];
      if (values[bp] !== undefined) {
        return values[bp]!;
      }
    }

    return defaultValue;
  }, [values, defaultValue, breakpoint]);
}

export default useViewport;
